import {
  posthog,
  useFeatureFlagEnabled,
  useFeatureFlagVariantKey,
} from '@/app-shell/flags/posthog-compat';
import { isTauri } from '@/tauri/utils';
import { isOnPrem } from '@/utils/buildProfile';
import { type CountryConfig, CountryConfigSchema } from '@/utils/country-config.types';
import { logger } from '@/utils/logger/logger';
import { ENV_NAME, getEnvironment } from '@heidi/api/config';
import { FEATURE_FLAG_DOMAIN } from '@heidi/runtime/feature-flags';
import { OpenFeature, useBooleanFlagValue } from '@openfeature/react-sdk';
import posthogJs from 'posthog-js';
import { isMobile } from 'react-device-detect';
import { z } from 'zod';

/**
 * Kill switch for modal pacing (quiet period / recording suppress / per-session
 * cap on non-essential launch surfaces). PostHog, default OFF. Fed
 * into `isModalPacingEnabledAtom` by `ModalPacingSync`.
 */
export const useIsModalPacingEnabled = () => {
  return useFeatureFlagEnabled('release-growth-modal-pacing') ?? false;
};

export const useExpandedSourceControlTrackingEnabled = () => {
  return (
    useFeatureFlagEnabled('release-growth-enable-extra-source-control-tracking') ?? false
  );
};

/**
 * Gates expanded analytics tracking for the in-session "Create" button —
 * a single `button_clicked` event with the resolved `creation_path`
 * (auto-mode vs default-template generation vs opening the template or
 * context modal). Defaults OFF so the Create-button tracking can be
 * rolled out independently from the sources popover and template
 * picker tracking flags.
 */
export const useExpandedCreateButtonTrackingEnabled = () => {
  return (
    useFeatureFlagEnabled('release-growth-enable-extra-create-button-tracking') ?? false
  );
};

export const useExpandedTemplatePickerTrackingEnabled = () => {
  return (
    useFeatureFlagEnabled('release-growth-enable-extra-template-picker-tracking') ?? false
  );
};

/**
 * When on, personalisation apply + learn are off by default and the user must
 * explicitly opt in via the personalisation modal. Non-hook so it can be
 * called from pure helpers (e.g. `isPersonalizationGateOpen`).
 */
export const isPersonalizationOptInEnabled = () => {
  return OpenFeature.getClient(FEATURE_FLAG_DOMAIN).getBooleanValue(
    'release-scribe-personalisation-opt-in',
    false
  );
};

/**
 * The PDF template demo on the community template details screen.
 *
 * MOVED to `@heidi/growth` by ruling 36's whole-class fan-out.
 */
/**
 * Gates DOCX template upload and DOCX export for form filling.
 * Using PostHog instead of Flagsmith (GRO — rollout reliability).
 */
export const useIsDocxFormEnabled = () => {
  return useFeatureFlagEnabled('release-growth-enable-docx-form') ?? false;
};

/**
 * GRO-3522: gates the full-page letterhead editor flow (route + Appearance entry).
 * Using PostHog instead of Flagsmith (GRO — rollout reliability), matching
 * `useIsDocxFormEnabled`.
 */
export const useIsLetterheadEditorEnabled = () => {
  return useFeatureFlagEnabled('release-growth-enable-letterhead-editor') ?? false;
};

const FIRST_NOTE_TEMPLATE_SELECTION_FLAG_KEY =
  'release-growth-enable-first-note-template-selection';

/**
 * Gates the first-note template-selection modal for new users.
 * PostHog (GRO — rollout reliability)
 * Rollout/experiment gate. Default OFF.
 */
export const useIsFirstNoteTemplateSelectionEnabled = () => {
  return useFeatureFlagEnabled(FIRST_NOTE_TEMPLATE_SELECTION_FLAG_KEY) ?? false;
};

/** Non-hook accessor for the launch gate that runs outside React. */
export const IS_FIRST_NOTE_TEMPLATE_SELECTION_ENABLED = () =>
  posthogJs.isFeatureEnabled(FIRST_NOTE_TEMPLATE_SELECTION_FLAG_KEY) ?? false;

/**
 * Feature flag for the external-share **inline expand** UI inside the share modal.
 *
 * When ON, clicking the "External access" empty state expands a full inline form
 * (recipients chip input + scope + expiry) and submits directly — replacing the
 * dashed hint button that used to just focus the user search input and funnel the
 * user through a secondary AlertDialog.
 *
 * Gated independently from session link sharing so we can
 * ship the UX redesign to a subset of orgs already on link sharing.
 */
export const useIsExternalShareInlineExpandEnabled = () => {
  return useFeatureFlagEnabled('release-scribe-external-share-inline-expand') ?? false;
};

/**
 * Feature flag for the external-share **reveal entry** redesign inside the
 * share modal's "External access" section.
 *
 * When ON, the section shows a single explainer card ("Share with people
 * outside your organisation") that reveals an inline email field on click;
 * sending hands off to the external-share confirmation dialog (scope + expiry).
 * When OFF, the legacy hint button is shown instead.
 *
 * PostHog (not Flagsmith) so backend evaluation stays consistent, mirroring
 * session link sharing. Gated independently from link
 * sharing so we can ramp this entry-point redesign on its own.
 */
export const useIsExternalShareRevealEntryEnabled = () => {
  return useFeatureFlagEnabled('release-scribe-external-share-reveal-entry') ?? false;
};

/**
 * When enabled, the "Create Template" modal shows three separate cards
 * (Note, Document, PDF form) instead of the default two-card layout.
 *
 * Using PostHog instead of Flagsmith because Flagsmith targeting currently lacks
 * the necessary cohorts for this experiment.
 */
export const useIsThreeCardTemplateModalEnabled = () => {
  return useFeatureFlagEnabled('release-growth-three-card-template-modal') ?? false;
};

/**
 * Last-week countdown for the 30-day complimentary Max usage: the sidebar day
 * count, the strip banner and the T-7 upgrade modal. Only reaches the opt-in
 * cohort (`release-heidi-2-opt-in` and `heidi_version === 2`). PostHog rollout
 * gate, default OFF — not an experiment, so no useManualExposure.
 */
export const useIsHeidi2TrialEndEnabled = () =>
  useFeatureFlagEnabled('release-growth-heidi-2-trial-end') ?? false;

/**
 * End of the new-user Heidi II Clinician trial: the T-7 strip banner and modal,
 * the sidebar countdown copy and the trial-ended modal. PostHog rollout gate,
 * default OFF — not an experiment, so no useManualExposure.
 */
export const useIsHeidi2ClinicianTrialEndEnabled = () =>
  useFeatureFlagEnabled('release-growth-heidi-2-clinician-trial-end') ?? false;

/**
 * The extra 15% yearly Max offer for members in a new-user Clinician trial: the
 * T-7 modal offer card and the seat checkout discount. PostHog rollout gate,
 * default OFF — keep it off until the backend applies the coupon to trial
 * members. Not an experiment, so no useManualExposure.
 */
export const useIsHeidi2ClinicianTrialOfferEnabled = () =>
  useFeatureFlagEnabled('release-growth-heidi-2-clinician-trial-offer') ?? false;

/**
 * Heidi II usage nudges: the per-tier sidebar usage card that opens once at 90%
 * and once at 100% of usage with its seat checkout links, and the Settings →
 * Billing current-plan 90%/100% usage banner. Opt-in cohort still applies via
 * credit-usage surface gates. PostHog rollout/kill switch, default OFF — not an
 * experiment, so no useManualExposure.
 */
export const useIsHeidi2UsageNudgesEnabled = () =>
  useFeatureFlagEnabled('release-growth-heidi-2-usage-nudges') ?? false;

/**
 * Gates the one-time announce spotlight on the session share button
 * introducing direct session sharing to active users.
 */
export const useIsShareSessionAnnounceSpotlightEnabled = () => {
  return useBooleanFlagValue('release-scribe-session-share-announce-spotlight', false);
};

/**
 * Enables the V2 signup-origin marker for a targeted cohort.
 *
 * This does not override the multivariate `release-new-onboarding-flow`
 * assignment; it only allows the targeted cohort to satisfy the localStorage
 * eligibility check after signup.
 */
export const useIsForceV2OnboardingLocalStorageOnEnabled = () => {
  return useFeatureFlagEnabled('release-force-v2-onboarding-localstorage-on') ?? false;
};

/**
 * The onboarding guided tour: the single note-edit accordion is promoted to
 * three dedicated screens (detail / artefacts / evidence) and the team-invite
 * step moves ahead of the transcription step.
 *
 * The `experimental-3` arm of `release-new-onboarding-flow` — the one flag that
 * picks the onboarding flow (see `GET_ONBOARDING_V2_VARIANT`). It replaced the
 * nested `exp-growth-onboarding-guided-tour` boolean so a user's flow is a single
 * sticky PostHog assignment rather than two flags ANDed together. The tour is an
 * expansion of the sample-conversation flow, so `usePersonalizationForm` still
 * requires a preview-capable viewport before it renders the tour screens.
 * Exposure is fired manually at the mic-test entry point
 * (`useGuidedTourExposure`) because analysis happens in Amplitude.
 */
export const useIsOnboardingGuidedTourEnabled = () => {
  return useFeatureFlagVariantKey(ONBOARDING_FLOW_FLAG_KEY) === 'experimental-3';
};

/**
 * Kill switch for onboarding resume-after-refresh (GRO-3866) — PostHog
 * `op-growth-disable-onboarding-resume`, boolean, default OFF (resume on).
 *
 * Deliberately a DISABLE flag rather than a release gate. Resuming is the fixed
 * behaviour and the flag exists only to switch it back off without a deploy, so
 * an unresolved flag (PostHog still loading, or blocked by an extension) must
 * fail open. It also has to: the wizard restores once at mount, so a flag that
 * arrives after first render could never retroactively enable a resume, and a
 * fail-closed default would silently reinstate the bug for anyone PostHog can't
 * reach.
 *
 * Flipping it ON stops both the restore and the writes, and clears any record
 * already in `sessionStorage`, from the next page load.
 */
export const useIsOnboardingResumeEnabled = () => {
  return !(useFeatureFlagEnabled('op-growth-disable-onboarding-resume') ?? false);
};

/**
 * Gates form editor (Field Recognition) multi-select on the PDF canvas as one
 * unit: marquee drag-select on the page background, shift-click add/remove,
 * group move (dragging any selected field moves the whole selection), and
 * Figma-style group resize via corner handles on the selection bounding box.
 * When OFF (default), the canvas keeps today's single-select behaviour exactly.
 * Rollout/kill gate, NOT an experiment — do not add useManualExposure.
 */
export const useIsFormEditorMultiSelectEnabled = () => {
  return useFeatureFlagEnabled('release-growth-form-editor-multi-select') ?? false;
};

/**
 * Gates form editor (Field Recognition) undo/redo as one unit: the Cmd/Ctrl+Z
 * and Shift+Cmd+Z / Ctrl+Y shortcuts plus the toolbar undo/redo buttons.
 * History recording itself always runs (cheap snapshot atoms) so flipping the
 * flag mid-session immediately exposes a working history.
 *
 * PostHog-backed (`useFeatureFlagEnabled`) so the rollout can be ramped and
 * targeted from PostHog. Defaults ON in the DEV environment (local + dev
 * deploy) so it is testable before the PostHog flag exists — mirrors
 * useIsMicTestLiveTranscriptionEnabled. Rollout/kill gate, NOT an
 * experiment — do not add useManualExposure.
 */
export const useIsFormEditorUndoRedoEnabled = () => {
  const flag = useFeatureFlagEnabled('release-growth-form-editor-undo-redo') ?? false;
  if (getEnvironment() === ENV_NAME.DEV) return true;
  return flag;
};

/**
 * Gates the form editor (Field Recognition) bulk field actions as
 * one unit: the Detected Fields header "⋯" menu (form-wide font size + field
 * type with confirmation dialogs), the per-field "Apply to all fields" offer
 * under the font size editor, and the sidebar layout rearrangement (add-field
 * button relocated to the page row, replacing the prev/next field chevrons).
 * When OFF (default), the legacy sidebar renders exactly as before — header
 * add-field button, chevron field navigation, no bulk actions. Rollout/kill
 * gate, NOT an experiment — do not add useManualExposure.
 */
export const useIsFormEditorBulkActionsEnabled = () => {
  return useFeatureFlagEnabled('release-growth-form-editor-bulk-actions') ?? false;
};

/**
 * Gates the Settings > Defaults "Default form field font size" control and
 * applying a saved default to newly detected / added fields. When the user has
 * not saved a preference, behaviour stays PDF Default (`null`) — no override.
 * When OFF, upload extraction leaves unset font sizes unchanged; manual add
 * keeps the legacy Auto (`0`) behaviour matching main.
 * Rollout/kill gate — not an experiment; no useManualExposure.
 */
export const useIsDefaultFormFieldFontSizeEnabled = () => {
  return useFeatureFlagEnabled('release-growth-default-form-field-font-size') ?? false;
};

// flag to open up Pyne for Germany
export const IS_PYNE_AVATAR = () => {
  if (posthog.isFeatureEnabled('pyne-change-onboarding-germany')) return 'germany';
  if (posthog.isFeatureEnabled('pyne-change-onboarding')) return 'global';
  return null;
};

/**
 * GRO-3264 / GRO-3265 / GRO-3281: Flagsmith (OpenFeature) multivariate A/B/C
 * experiment for the post-transcription note-entry surface — what happens the
 * first time a clinician would choose a template after recording stops.
 * - control: current behavior (full picker / existing post-stop logic)
 * - auto_generate: auto-generate in Auto mode, no picker (Variant A / GRO-3264)
 * - simple_picker: stripped-down picker — search + Auto + suggestions + saved
 *   templates + free-text Create, no upgrade/create-template/filter chrome
 *   (Variant B / GRO-3265)
 * - auto_first_picker: minimal Auto-first picker — Auto-only until typing, no
 *   saved templates list (Variant C / GRO-3281)
 *
 * Enrolment only changes behavior for users who would otherwise land on an
 * Auto note or the full picker: a specific (non-Auto) saved default template
 * still takes precedence, and such users stream their default before the
 * experiment block runs (so they get no `$exposure`). An explicit per-session
 * template/Auto selection is likewise respected (all handled upstream in
 * `_actionPostRecording`).
 *
 * Flagsmith flag shape: the flag's enabled state is enrolment (read via
 * {@link IS_POST_TRANSCRIPTION_NOTE_ENTRY_ASSIGNED}); its string value is the
 * variant (read via {@link GET_POST_TRANSCRIPTION_NOTE_ENTRY_VARIANT}).
 */
export type PostTranscriptionNoteEntryVariant =
  | 'control'
  | 'auto_generate'
  | 'simple_picker'
  | 'auto_first_picker';

export const POST_TRANSCRIPTION_NOTE_ENTRY_FLAG_KEY =
  'release-growth-post-transcription-note-entry';

export const GET_POST_TRANSCRIPTION_NOTE_ENTRY_VARIANT =
  (): PostTranscriptionNoteEntryVariant => {
    const schema = z
      .enum(['control', 'auto_generate', 'simple_picker', 'auto_first_picker'])
      .catch('control');
    return schema.parse(
      OpenFeature.getClient(FEATURE_FLAG_DOMAIN).getStringValue(
        POST_TRANSCRIPTION_NOTE_ENTRY_FLAG_KEY,
        'control'
      )
    );
  };

/**
 * Whether the user is enrolled in the post-transcription note-entry experiment
 * (the Flagsmith flag is enabled for them). Used to gate manual exposure so we
 * only record exposure for assigned users; non-enrolled users fall through to
 * the unchanged control behavior with no exposure event.
 */
export const IS_POST_TRANSCRIPTION_NOTE_ENTRY_ASSIGNED = (): boolean => {
  return OpenFeature.getClient(FEATURE_FLAG_DOMAIN).getBooleanValue(
    POST_TRANSCRIPTION_NOTE_ENTRY_FLAG_KEY,
    false
  );
};

export const IS_SELF_ONBOARDING_MODAL_QUEUED = () => {
  // FLAG_CLEANUP: hardcoded false because the PostHog flag is no longer configured.
  return false;
};

/**
 * GRO-3284: Flagsmith (OpenFeature) multivariate A/B/C experiment for the
 * post-note quality experience — how a clinician is coached/rewarded after a
 * note is generated. Replaces the standalone `release-growth-post-note-quality-panel`
 * boolean: the coaching sidebar (GRO-3268/3269/3270) is now Arms B/C of this test.
 * - control: existing gamified trial only, no coaching sidebar.
 * - sidebar_only: coaching sidebar only; gamified trial suppressed. Completing
 *   2 sidebar tasks grants +7 trial days, all 5 grants +7 more (=+14).
 * - sequential: coaching sidebar first (+7/+7); once all sidebar tasks are
 *   complete the gamified trial is introduced (its own +14 by existing logic).
 *
 * Flag shape: enabled state is enrolment ({@link IS_POST_NOTE_QUALITY_EXPERIMENT_ASSIGNED});
 * the string value is the variant ({@link GET_POST_NOTE_QUALITY_EXPERIMENT_VARIANT}).
 */
export type PostNoteQualityExperimentVariant = 'control' | 'sidebar_only' | 'sequential';

export const POST_NOTE_QUALITY_EXPERIMENT_FLAG_KEY =
  'release-growth-post-note-quality-experience';

export const GET_POST_NOTE_QUALITY_EXPERIMENT_VARIANT =
  (): PostNoteQualityExperimentVariant => {
    const schema = z.enum(['control', 'sidebar_only', 'sequential']).catch('control');
    return schema.parse(
      OpenFeature.getClient(FEATURE_FLAG_DOMAIN).getStringValue(
        POST_NOTE_QUALITY_EXPERIMENT_FLAG_KEY,
        'control'
      )
    );
  };

/**
 * Whether the user is enrolled in the post-note quality experiment (the
 * Flagsmith flag is enabled for them). Gates manual exposure so only assigned
 * users record `$exposure`, and gates the arm-specific behaviour below.
 */
export const IS_POST_NOTE_QUALITY_EXPERIMENT_ASSIGNED = (): boolean => {
  return OpenFeature.getClient(FEATURE_FLAG_DOMAIN).getBooleanValue(
    POST_NOTE_QUALITY_EXPERIMENT_FLAG_KEY,
    false
  );
};

/**
 * GRO-3268 "Improve your notes" coaching sidebar visibility. Shown for the
 * `sidebar_only` and `sequential` arms of the post-note quality experiment
 * (GRO-3284). The session page surfaces a right-docked checklist of
 * note-improvement tasks after a note is generated.
 */
export const IS_POST_NOTE_QUALITY_PANEL_ENABLED = () => {
  if (!IS_POST_NOTE_QUALITY_EXPERIMENT_ASSIGNED()) return false;
  const variant = GET_POST_NOTE_QUALITY_EXPERIMENT_VARIANT();
  return variant === 'sidebar_only' || variant === 'sequential';
};

export const IS_REMOVE_DEMO_OPTION_ENABLED = () => {
  return posthog.isFeatureEnabled('release-remove-demo-option');
};

export const IS_BINARY_CSAT_ENABLED = () => {
  return posthog.isFeatureEnabled('release-binary-csat') ?? false;
};

/** Shipped 100% — PostHog `release-specialty-in-organisation-survey`. UI call sites untouched (multi-pod). */
export const IS_SPECIALTY_IN_ORGANISATION_SURVEY_ENABLED = () => {
  return true;
};

/** @deprecated Use getPhoneNumberVariantForCountry instead */
export const GET_PHONE_NUMBER_IN_SIGNUP_US_VARIANT = ():
  | 'hidden'
  | 'required'
  | 'optional' => {
  const schema = z.enum(['hidden', 'required', 'optional']).catch('hidden');
  return schema.parse(posthog.getFeatureFlag('release-phone-number-in-signup-us'));
};

export const GET_COUNTRY_CONFIG = (): CountryConfig | null => {
  try {
    let flagPayload = posthog.getFeatureFlagPayload('country-config');
    if (!flagPayload) return null;

    // PostHog may return the payload as a JSON string instead of a parsed object
    if (typeof flagPayload === 'string') {
      try {
        flagPayload = JSON.parse(flagPayload) as typeof flagPayload;
      } catch {
        logger.error('Failed to parse country config payload string', {
          payload: flagPayload,
        });
        return null;
      }
    }

    const parseResult = CountryConfigSchema.safeParse(flagPayload);
    if (!parseResult.success) {
      logger.error('Invalid country config payload from PostHog', {
        error: parseResult.error,
        payload: flagPayload,
      });
      return null;
    }
    return parseResult.data;
  } catch (error) {
    logger.error('Error fetching country config from PostHog', { error });
    return null;
  }
};

/** @deprecated Replaced by `<DiscoveryCallNavItem />`, which derives visibility
 *  from `country-config.discoveryCallLink`. Remove in the cleanup PR. */
export const IS_ANDRES_CTA = () => {
  return posthog.isFeatureEnabled('release-german-book-call');
};

/** @deprecated Replaced by `<DiscoveryCallNavItem />`, which derives visibility
 *  from `country-config.discoveryCallLink`. Remove in the cleanup PR. */
export const IS_FRANCE_CTA = () => {
  return posthog.isFeatureEnabled('release-france-book-call');
};

/** @deprecated Replaced by `<DiscoveryCallNavItem />`, which derives visibility
 *  from `country-config.discoveryCallLink`. Remove in the cleanup PR. */
export const IS_SPANISH_CTA = () => {
  return posthog.isFeatureEnabled('release-spanish-book-call');
};

export const IS_GERMAN_GET_STARTED_CTA = () => {
  return posthog.isFeatureEnabled('release-german-get-started-cta');
};

export const IS_ONBOARDING_COMPLIANCE_US_ENABLED = () => {
  return posthog.isFeatureEnabled('release-onboarding-compliance-us');
};

export const IS_ONBOARDING_COMPLIANCE_DE_ENABLED = () => {
  return posthog.isFeatureEnabled('release-onboarding-compliance-de');
};

export const IS_UK_PRACTICE_SEARCH_MANDATORY_ENABLED = () => {
  return posthog.isFeatureEnabled('release-uk-practice-search-mandatory');
};

export const IS_JURISDICTION_IN_ONBOARDING_ENABLED = () => {
  // FLAG_CLEANUP: hardcoded false because the PostHog flag is no longer configured.
  return false;
};

/**
 * The one flag that picks the onboarding flow (`release-new-onboarding-flow`).
 * Every variant is a complete flow — there are no sub-flags underneath:
 *
 * - `control`        → the legacy single-form sign-up.
 * - `experimental-2` → the sample-conversation flow (current production).
 * - `experimental-3` → the guided tour: the sample-conversation flow with the
 *                      note-edit accordion split into three tour screens and
 *                      the team invite moved ahead of transcription.
 *
 * The retired `experimental` value (the v2 template-creation flow) parses as
 * `control`, like any other unknown value.
 */
export type OnboardingV2Variant = 'control' | 'experimental-2' | 'experimental-3';

export const ONBOARDING_FLOW_FLAG_KEY = 'release-new-onboarding-flow';

// PostHog is the source of truth for pre-auth market eligibility and rollout.
// A missing or unmatched flag resolves to `control`.

export const IS_ONBOARDING_V2_ENABLED = () => {
  if (isTauri()) return false;
  return GET_ONBOARDING_V2_VARIANT() !== 'control';
};

export const GET_ONBOARDING_V2_VARIANT = (): OnboardingV2Variant => {
  if (isTauri()) return 'control';
  const schema = z.enum(['control', 'experimental-2', 'experimental-3']).catch('control');
  const variant = schema.parse(posthog.getFeatureFlag(ONBOARDING_FLOW_FLAG_KEY));

  if (variant !== 'control') {
    // Both treatment flows are desktop-web only.
    if (typeof window === 'undefined' || isMobile) return 'control';
  }

  return variant;
};

/** True for any onboarding-v2 treatment (`experimental-2` or `experimental-3`). */
export const IS_ONBOARDING_V2_TREATMENT = () => {
  if (!IS_ONBOARDING_V2_ENABLED()) return false;
  return GET_ONBOARDING_V2_VARIANT() !== 'control';
};

/**
 * When true, surfaces that would render the V2 `<SignUpPage>` OR the legacy
 * `<UnauthenticatedDemoFlow>` instead bypass them and redirect straight to
 * the Auth0 register screen. Variant-agnostic by design — works regardless
 * of `release-new-onboarding-flow` rollout state, so the marketing-attributed
 * `/onboarding` entry funnels every visitor to auth without waiting for V2
 * to roll out.
 *
 * Forced off outside cloud web, regardless of PostHog rollout: the Auth0
 * browser redirect is unsafe under the Tauri auth plugin, and the on-prem
 * profile authenticates against Keycloak, not Auth0. On-prem is checked
 * separately from Tauri because the profile can also be served as web, where
 * `isTauri()` is false.
 */
export const IS_ONBOARDING_SIGNUP_BYPASS_ENABLED = () => {
  if (isTauri() || isOnPrem) return false;
  return posthog.isFeatureEnabled('release-onboarding-signup-bypass');
};

/**
 * Sidebar Referral Button: Shows a "Refer and earn" button in the sidebar for paid users.
 */
export const IS_SIDEBAR_REFERRAL_BUTTON_ENABLED = () => {
  return posthog.isFeatureEnabled('release-sidebar-referral-button');
};

/**
 * Gates the LOAD-BEARING browser-side `signup_conversion` marketing event (the
 * Meta conversion pair with server-side `sign_up_successful`). Off by default.
 * This is the SINGLE gate for the event — it controls rollout/kill-switch and
 * environment scoping: enable it ONLY in the production Flagsmith environment so
 * staging/dev never forward signups to Meta. See
 * `analyticsScribeModel.trackSignUpSuccessfulWebAuth`.
 */
export const IS_SIGNUP_CONVERSION_EVENT_ENABLED = () =>
  OpenFeature.getClient(FEATURE_FLAG_DOMAIN).getBooleanValue(
    'op-signup-conversion-event',
    false
  );
